import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { apiFetchJson } from "@rook/shared/lib/apiFetch";
import type { Environment } from "@rook/shared/lib/appContext";

import type { TimezoneSettings } from "./types";

const timezoneQueryKey = (env: Environment) =>
  ["timezone", "current", env] as const;

const timezonePath = (env: Environment) =>
  `/api/proxy/clients/${env}/customize/time_zone`;

/**
 * Fetch the current timezone configuration for the given env.
 * Mirrors the legacy server action's call to
 * `GET /customize/time_zone` on rook_clients_api, just routed
 * through the v3 proxy Lambda.
 */
export const useTimezone = (env: Environment) =>
  useQuery({
    queryKey: timezoneQueryKey(env),
    queryFn: () => apiFetchJson<TimezoneSettings>(timezonePath(env)),
  });

/**
 * Update the timezone configuration. PUT replaces all three fields
 * (time_zone, offset, sleep_summary) — the upstream API expects the
 * full payload even though `offset` is derivable from `time_zone`.
 * On success, invalidate the query so the next read picks up the
 * persisted value (and the page repaints the "configured" pill).
 */
export const useUpdateTimezone = (env: Environment) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: TimezoneSettings) =>
      apiFetchJson<{ ok?: boolean }>(timezonePath(env), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timezoneQueryKey(env) });
    },
  });
};
